import type { AuthObservabilityService, ObservabilityCommand } from '../types';
import { ObservabilityEvent, ErrorCategory, Severity } from '../constants';
import { extractErrorMessage } from '../shared/extract-error-message';
import { detectErrorCategory } from './detect-error-category';

type AuthMethod = 'email' | 'google' | 'facebook' | 'apple';

export type TrackEventFn = (
  eventName: string,
  customAttributes?: Record<string, unknown>,
) => void;

export function detectAuthMethod(properties: Record<string, unknown>): AuthMethod {
  const provider = properties.provider;
  if (typeof provider === 'string') {
    if (provider === 'google' || provider === 'facebook' || provider === 'apple') {
      return provider;
    }
  }
  return 'email';
}

/**
 * Only these event types are forwarded to the tracking provider.
 * Success events and user-visible validation errors are intentionally excluded
 * to avoid noise — the UI already surfaces those to the user.
 * All failure events are included so no error slips by unnoticed.
 */
const CRITICAL_EVENTS = new Set<ObservabilityCommand['type']>([
  'bootstrap_failure',
  'session_failure',
  'signout_failure',
  'delete_account_failure',
  'social_signin_failure',
  'clear_stale_session_failure',
  'register_failure',
  'signin_failure',
  'change_password_failure',
  'submit_phone_failure',
  'resend_code_failure',
  'email_lookup_failure',
  'idp_probe_failure',
  'update_profile_failure',
  'update_email_failure',
  'update_email_confirmation_failure',
]);

export class AuthObservabilityServiceImpl implements AuthObservabilityService {
  private readonly trackEvent: TrackEventFn;

  constructor(trackEvent: TrackEventFn) {
    this.trackEvent = trackEvent;
  }

  dispatch(event: ObservabilityCommand): void {
    if (!CRITICAL_EVENTS.has(event.type)) return;

    switch (event.type) {
      case 'register_failure':
        this.trackEvent(ObservabilityEvent.RegisterFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: detectErrorCategory(event.error, 'register'),
          authMethod: event.authMethod,
          severity: Severity.Critical,
        });
        break;
      case 'register_success':
        this.trackEvent(ObservabilityEvent.RegisterSucceeded, { authMethod: event.authMethod });
        break;
      case 'signin_failure':
        this.trackEvent(ObservabilityEvent.SignInFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: detectErrorCategory(event.error, 'signin'),
          authMethod: event.authMethod,
          severity: Severity.Critical,
        });
        break;
      case 'signin_success':
        this.trackEvent(ObservabilityEvent.SignInSucceeded, { authMethod: event.authMethod });
        break;
      case 'social_signin_success':
        this.trackEvent(ObservabilityEvent.SocialSignInSucceeded, { provider: event.provider });
        break;
      case 'social_signin_failure':
        this.trackEvent(ObservabilityEvent.SocialSignInFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.UnknownError,
          provider: event.provider,
          severity: Severity.Critical,
        });
        break;
      case 'change_password_success':
        this.trackEvent(ObservabilityEvent.ChangePasswordSucceeded);
        break;
      case 'change_password_failure':
        this.trackEvent(ObservabilityEvent.ChangePasswordFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.UnknownError,
          severity: Severity.Critical,
        });
        break;
      case 'submit_phone_success':
        this.trackEvent(ObservabilityEvent.SubmitPhoneSucceeded);
        break;
      case 'submit_phone_failure':
        this.trackEvent(ObservabilityEvent.SubmitPhoneFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.UnknownError,
          severity: Severity.Critical,
        });
        break;
      case 'session_refresh_success':
        this.trackEvent(ObservabilityEvent.SessionRefreshSucceeded);
        break;
      case 'session_failure':
        this.trackEvent(ObservabilityEvent.SessionFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.UnknownError,
          severity: Severity.Critical,
        });
        break;
      case 'delete_account_success':
        this.trackEvent(ObservabilityEvent.DeleteAccountSucceeded);
        break;
      case 'delete_account_failure':
        this.trackEvent(ObservabilityEvent.DeleteAccountFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.UnknownError,
          severity: Severity.Critical,
        });
        break;
      case 'email_lookup_failure':
        this.trackEvent(ObservabilityEvent.EmailLookupFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.ApiCallFailed,
          severity: Severity.Critical,
        });
        break;
      case 'idp_probe_failure':
        this.trackEvent(ObservabilityEvent.IdpProbeFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.ApiCallFailed,
          severity: Severity.Critical,
        });
        break;
      case 'signout_failure':
        this.trackEvent(ObservabilityEvent.SignOutFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.UnknownError,
          severity: Severity.Critical,
        });
        break;
      case 'resend_code_failure':
        this.trackEvent(ObservabilityEvent.ResendCodeFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.ApiCallFailed,
          email: event.email,
          severity: Severity.Critical,
        });
        break;
      case 'clear_stale_session_failure':
        this.trackEvent(ObservabilityEvent.ClearStaleSessionFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.UnknownError,
          severity: Severity.Critical,
        });
        break;
      case 'bootstrap_failure':
        this.trackEvent(ObservabilityEvent.BootstrapFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.UnknownError,
          severity: Severity.Critical,
        });
        break;
      case 'update_profile_failure':
        this.trackEvent(ObservabilityEvent.UpdateProfileFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.ApiCallFailed,
          severity: Severity.Critical,
        });
        break;
      case 'update_email_failure':
        this.trackEvent(ObservabilityEvent.UpdateEmailFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.ApiCallFailed,
          severity: Severity.Critical,
        });
        break;
      case 'update_email_confirmation_failure':
        this.trackEvent(ObservabilityEvent.UpdateEmailConfirmationFailed, {
          errorMessage: extractErrorMessage(event.error),
          errorCategory: ErrorCategory.ApiCallFailed,
          severity: Severity.Critical,
        });
        break;
    }
  }
}
